import type { PaymentMethodCreateParams } from '@stripe/stripe-js';

/**
 * Stripe の card brand 値（`visa` / `mastercard` 等）に対応する日本語表示名。
 *
 * @see https://docs.stripe.com/api/cards/object#card_object-brand
 */
export const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'ビザ',
  mastercard: 'マスターカード',
  amex: 'アメリカン・エキスプレス',
  jcb: 'JCB',
  diners: 'ダイナースクラブ',
  discover: 'ディスカバー',
  unionpay: '銀聯',
};

/** エラー文言 */
export const ERROR_MESSAGE = {
  /** 404: クレジットカード払いの契約ではない（Stripe Customer 未結線） */
  notCardPayment: 'クレジットカード払いのご契約ではないため、カード情報を変更できません。',
  /** 404 以外（502 等。Stripe 側のエラーを含む） */
  unexpected: 'カード情報の変更を開始できませんでした。時間をおいて再度お試しください。',
  /**
   * カード登録（elements.submit / confirmSetup）が失敗し、Stripe がエラーメッセージを返さなかった場合。
   *
   * カード不正・decline 等の想定内のエラーでは Stripe が日本語（locale: 'ja'）の
   * `error.message` を返すため、そちらをそのまま表示する。
   */
  cardRegistrationFailed: 'カード情報を登録できませんでした。時間をおいて再度お試しください。',
} as const;

/**
 * カード登録成功時の案内文言
 *
 * 新カードの既定化は Stripe の `setup_intent.succeeded` Webhook が非同期で行うため、
 * confirmSetup 成功直後に画面上のカード情報を再取得しても旧カードが返る可能性がある。
 * そのため「即時反映されない」ことをユーザーに伝える。
 */
export const SUCCESS_MESSAGE = 'カード情報を登録しました。反映まで時間がかかる場合があります。';

/**
 * confirmSetup に渡す請求先住所
 *
 * `PaymentElement` の `fields.billingDetails.address` で `'never'` を指定したフィールドは、
 * `confirmSetup` 時に自前で渡す義務が発生する。義務は「Stripe が実際に入力欄を描画したか」ではなく
 * **`fields` に `'never'` と指定したかどうか**で判定される（渡さないと `IntegrationError` になる）。
 *
 * - `country`: PaymentMethod に付く請求先住所の国。ここで渡す国は使えるカードの発行国を制限しない
 * - `postal_code`: 収集しない（Figma に入力欄がない）ため `null` を渡して未収集を明示する
 *
 * キー名は confirmSetup にそのまま渡せるよう Stripe API の snake_case に合わせ、`satisfies` で
 * 型を固定している。`Address` は全プロパティが optional なため、`postalCode` のように綴りを
 * 間違えても型エラーにならず実行時の `IntegrationError` になる。それを compile 時に落とすため。
 *
 * @see https://docs.stripe.com/js/setup_intents/confirm_setup
 */
export const BILLING_ADDRESS = {
  country: 'JP',
  postal_code: null,
} as const satisfies PaymentMethodCreateParams.BillingDetails.Address;
